import React, { useState } from 'react';
import { Card, Row, Col, Button } from 'react-bootstrap';
import { PieChart, Pie, Cell, ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';

// Mini pie chart component similar to RatioPieChart in the pasted code
const OutageDataCard = ({ outageData }) => {
  const [showDetails, setShowDetails] = useState(false);
  const latest = outageData[outageData.length - 1];


  return (
    <Row className="mb-4">
      
      <Col>
        <Card className="shadow border-0">
          <Card.Header className="bg-danger text-white">
            <h4 className="mb-0">Outage Status by Town</h4>
            <div className="text-muted small">Last updated: {latest ? new Date(latest.last_update).toLocaleString() : 'No data'}</div>            
          </Card.Header>
          <Card.Body>
            {/* 1st Row: Overall pie chart */}
            <Row className="mb-4">

              <Col xs={12} md={8} lg={6} className="mx-auto">
                <Card className="h-100 border-0 shadow-sm">
                  <Card.Body>
                    <div className="text-center mb-2" style={{ fontWeight: 600 }}>Overall Service Status</div>
                    {(() => {
                      if (outageData.length === 0) return <div className="text-center text-muted">No data</div>;
                      // const latest = outageData[outageData.length - 1];
                      let total = 0, out = 0, planned = 0;
                      ['san_juan', 'mayaguez', 'ponce', 'arecibo', 'carolina', 'caguas', 'bayamon'].forEach(town => {
                        total += Number(latest[`total_customers_${town}`]) || 0;
                        out += Number(latest[`out_of_service_${town}`]) || 0;
                        planned += Number(latest[`planned_upgrades_${town}`]) || 0;
                      });
                      const withService = Math.max(total - out - planned, 0);
                      const pieData = [
                        { name: 'With Service', value: withService },
                        { name: 'Out of Service', value: out },
                        { name: 'Planned Upgrades', value: planned }
                      ];
                      const COLORS = ['#28a745', '#dc3545', '#ffc107'];
                      return (
                        <>
                          <ResponsiveContainer width="100%" height={180}>
                            <PieChart>
                              <Pie
                                data={pieData}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={70}
                                innerRadius={40}
                                labelLine={false}
                              >
                                {pieData.map((entry, idx) => (
                                  <Cell key={entry.name} fill={COLORS[idx]} />
                                ))}
                              </Pie>
                            </PieChart>
                          </ResponsiveContainer>
                          <div className="text-center small mt-2">
                            <span className="me-2" style={{ color: COLORS[0] }}>With Service: <b>{withService.toLocaleString()}</b></span><br />
                            <span className="me-2" style={{ color: COLORS[1] }}>Out: <b>{out.toLocaleString()}</b></span><br />
                            <span style={{ color: COLORS[2] }}>Planned: <b>{planned.toLocaleString()}</b></span>
                          </div>
                        </>
                      );
                    })()}
                  </Card.Body>
                </Card>
              </Col>
              <Col>
                <Card className="h-100 border-0 shadow-sm">
                  <Card.Body>
                    <div className="text-center mb-2" style={{ fontWeight: 600 }}>Customers Out of Service Over Time</div>
                    <div style={{ width: '100%', height: 250 }}>
                      {outageData.length > 0 ? (
                        <ResponsiveContainer>
                          <LineChart
                            data={outageData.map(row => {
                              let out = 0;
                              ['san_juan', 'mayaguez', 'ponce', 'arecibo', 'carolina', 'caguas', 'bayamon'].forEach(town => {
                                out += Number(row[`out_of_service_${town}`]) || 0;
                              });
                              return {
                                timestamp: new Date(row.timestamp).getTime(),
                                formattedTimestamp: new Date(row.timestamp).toLocaleString(),
                                out_of_service: out
                              };
                            })}
                            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              dataKey="timestamp"
                              type="number"
                              domain={['auto', 'auto']}
                              scale="time"
                              tickFormatter={ts => new Date(ts).toLocaleTimeString()}
                            />
                            <YAxis />
                            <Tooltip
                              labelFormatter={ts => new Date(ts).toLocaleString()}
                              formatter={(value) => value.toLocaleString()}
                            />
                            <Legend />
                            <Line
                              type="monotone"
                              dataKey="out_of_service"
                              name="Total Out of Service"
                              stroke="#dc3545"
                              strokeWidth={2}
                              dot={false}
                              isAnimationActive={false}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="text-center text-muted">No data</div>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            {/* Toggle Button */}
            <div className="text-center mb-3">
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => setShowDetails((prev) => !prev)}
              >
                {showDetails ? 'Hide Details' : 'Show Details'}
              </Button>
            </div>
            {/* 2nd Row: Pie charts for each town */}
            {showDetails && (
              <Row className="mb-4">
                {['san_juan', 'mayaguez', 'ponce', 'arecibo', 'carolina', 'caguas', 'bayamon'].map((town) => {
                  const latest = outageData.length > 0 ? outageData[outageData.length - 1] : null;
                  if (!latest) return (
                    <Col key={town} xs={12} md={6} lg={3} className="mb-3">
                      <Card className="h-100 border-0 shadow-sm">
                        <Card.Body>
                          <div className="text-center text-muted">No data</div>
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                  const total = Number(latest[`total_customers_${town}`]) || 0;
                  const out = Number(latest[`out_of_service_${town}`]) || 0;
                  const planned = Number(latest[`planned_upgrades_${town}`]) || 0;
                  const withService = Math.max(total - out - planned, 0);
                  const pieData = [
                    { name: 'With Service', value: withService },
                    { name: 'Out of Service', value: out },
                    { name: 'Planned Upgrades', value: planned }
                  ];
                  const COLORS = ['#28a745', '#dc3545', '#ffc107'];
                  return (
                    <Col key={town} xs={12} md={6} lg={3} className="mb-3">
                      <Card className="h-100 border-0 shadow-sm">
                        <Card.Body>
                          <div className="text-center mb-2" style={{ fontWeight: 600, textTransform: 'capitalize' }}>{town.replace('_', ' ')}</div>
                          <ResponsiveContainer width="100%" height={120}>
                            <PieChart>
                              <Pie
                                data={pieData}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={40}
                                innerRadius={22}
                                labelLine={false}
                              >
                                {pieData.map((entry, idx) => (
                                  <Cell key={entry.name} fill={COLORS[idx]} />
                                ))}
                              </Pie>
                            </PieChart>
                          </ResponsiveContainer>
                          <div className="text-center small mt-2">
                            <span className="me-2" style={{ color: COLORS[0] }}>With Service: <b>{withService.toLocaleString()}</b></span><br />
                            <span className="me-2" style={{ color: COLORS[1] }}>Without Service: <b>{out.toLocaleString()}</b></span><br />
                            <span style={{ color: COLORS[2] }}>Planned Upgrades: <b>{planned.toLocaleString()}</b></span>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            )}
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default OutageDataCard;